import { Ohje } from "./Ohje";
import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Kayttaja, type Sertifikaatti } from "./api";
import { haeBrandi } from "./brandi";
import { t, esim, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Yrityksen tiedot.

   Talon omat tiedot (nimi, Y-tunnus, osoite, puhelin, verkkosivu) ja
   sertifikaatit (ISO 3834, PED…). Näytetään tarjousdokumentissa
   lähettäjänä. Vain pääkäyttäjälle.
   ═══════════════════════════════════════════════════════════ */

/**
 * `sertifikaatit`: talon tiedot ovat ydintä (brändi, white-label), mutta
 * sertifikaattilista on tarjousdomainia ja sen reitit ovat nyt
 * `tarjous`-portin takana. Ilman tätä lippua lohko näyttäisi ilman
 * tarjousmoduulia tyhjää listaa ja lomakkeen, joka kaatuu 404:ään.
 */
export default function Yrityksentiedot(
  { kayttaja, sertifikaatit }: { kayttaja: Kayttaja; sertifikaatit: boolean }
) {
  const paakayttaja = kayttaja.roolit.includes("paakayttaja");
  const [tiedot, setTiedot] = useState({ nimi: "", ytunnus: "", osoite: "", puhelin: "", verkkosivu: "", yhteysosoite: "" });
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tallentaa, setTallentaa] = useState(false);

  useEffect(() => {
    void haeBrandi().then((b) => setTiedot({
      nimi: b.nimi ?? "", ytunnus: b.ytunnus ?? "", osoite: b.osoite ?? "",
      puhelin: b.puhelin ?? "", verkkosivu: b.verkkosivu ?? "",
      yhteysosoite: (b as { yhteysosoite?: string }).yhteysosoite ?? "",
    }));
  }, []);

  const tallenna = async () => {
    setTallentaa(true); setIlmoitus(null);
    try { await api.paivitaYrityksenTiedot(tiedot); setIlmoitus(t("yri.tallennettu")); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallentaa(false); }
  };

  if (!paakayttaja) {
    return (
      <section className="panel">
        <div className="panel__otsikko"><h2>{t("valikko.yrityksen_tiedot")}</h2></div>
        <p className="dim">{t("yri.vain_paakayttaja")}</p>
      </section>
    );
  }

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.yrityksen_tiedot")}</h1></div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: PATCH /api/brandi ja sertifikaattien lisäys/poisto: vaadiRooli(paakayttaja). */}
      <Ohje
        kuka={<>{t("yri.ohje.kuka")}</>}
        luvut={<>{t("yri.ohje.luvut")}</>} />
      <section className="panel">
        <p className="dim">{t("yri.lahettaja")}</p>
        <div className="asiakas__lomake">
          {([["nimi","yri.yritys"],["ytunnus","yri.ytunnus"],["osoite","yri.osoite"],
             ["puhelin","yri.puhelin"],["verkkosivu","yri.verkkosivu"],["yhteysosoite","yri.yhteysosoite"]] as const).map(([k, l]) => (
            <label key={k}>{t(l)}
              <input value={(tiedot as any)[k]} onChange={(e) => setTiedot((v) => ({ ...v, [k]: e.target.value }))} />
            </label>
          ))}
          <button className="nappi nappi--paa" disabled={tallentaa} onClick={tallenna}>
            {tallentaa ? t("pu.tallennetaan") : t("kuittaus.tallenna")}
          </button>
        </div>
        {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

        {sertifikaatit && <Sertifikaatit />}
      </section>
    </>
  );
}

function Sertifikaatit() {
  const [lista, setLista] = useState<Sertifikaatti[] | null>(null);
  const [nimi, setNimi] = useState("");
  const [tunnus, setTunnus] = useState("");
  const [myontaja, setMyontaja] = useState("");
  const [voimassa, setVoimassa] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setLista(await api.sertifikaatit()); } catch { setLista([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  const lisaa = async () => {
    if (!nimi.trim()) { setVirhe(t("yri.anna_nimi")); return; }
    try {
      await api.luoSertifikaatti({
        nimi: nimi.trim(), tunnus: tunnus.trim() || null,
        myontaja: myontaja.trim() || null, voimassa_asti: voimassa || null,
      });
      setNimi(""); setTunnus(""); setMyontaja(""); setVoimassa(""); setVirhe(null);
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.lisays_virhe")); }
  };

  return (
    <div className="asetukset__lohko">
      <h3>{t("yri.sertifikaatit")}</h3>
      <p className="dim">{t("yri.sert_selite")}</p>
      {lista && lista.length > 0 && (
        <ul className="kohdelista">
          {lista.map((s) => (
            <li key={s.id}>
              <span><strong>{s.nimi}</strong>{s.tunnus ? ` · ${s.tunnus}` : ""}
                {s.myontaja ? <span className="dim"> · {s.myontaja}</span> : null}
                {s.voimassa_asti ? <span className="dim"> · {t("yri.voimassa", { pvm: naytaPvm(s.voimassa_asti) ?? "" })}</span> : null}
              </span>
              <button className="nappi nappi--kevyt"
                      onClick={async () => { if (!confirm(t("yri.poista_kysymys", { nimi: s.nimi }))) return; await api.poistaSertifikaatti(s.id); await lataa(); }}>{t("liite.poista")}</button>
            </li>
          ))}
        </ul>
      )}
      {lista && lista.length === 0 && <p className="dim">{t("yri.ei_sertifikaatteja")}</p>}
      <div className="asiakas__lomake">
        <label>{t("iv.nimi")}<input value={nimi} placeholder={esim("SFS-EN ISO 3834-2")} onChange={(e) => setNimi(e.target.value)} /></label>
        <label>{t("iv.tunnus")}<input value={tunnus} onChange={(e) => setTunnus(e.target.value)} /></label>
        <label>{t("yri.myontaja")}<input value={myontaja} onChange={(e) => setMyontaja(e.target.value)} /></label>
        <label>{t("yri.voimassa_asti")}<input type="date" value={voimassa} onChange={(e) => setVoimassa(e.target.value)} /></label>
        <button className="nappi nappi--paa" onClick={lisaa}>{t("yri.lisaa")}</button>
      </div>
      {virhe && <p className="huom">{virhe}</p>}
    </div>
  );
}
